import React from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { skillService } from "./skillService";
import { useToaster } from "contexts/toasterContext";
import { preventSpecialCharKeyDownInTextfieldAllowPlus } from "util/preventSpecialCharInput";

const schema = yup.object({
  skill: yup
    .array()
    .of(
      yup.object({
        skill: yup
          .string()
          .required("Skill name is required")
          .test(
            "not-only-spaces",
            "Skill name cannot contain only spaces",
            (value) => {
              // If there's text, but trimming it leaves nothing, it's just spaces
              if (value && value.trim().length === 0) {
                return false;
              }
              return true;
            }
          ),
      })
    )
    .min(1, "At least one skill is required"),
});

interface Props {
  onSuccess: () => void;
}

export default function AddSkillForm({ onSuccess }: Props) {
  const { addToast } = useToaster();

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      skill: [{ skill: "" }],
    },
    mode: "onChange",
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "skill",
  });

  const watchedSkills = watch("skill");

  // Check if any field is empty
  const hasEmptyField = watchedSkills?.some(
    (item) => !item?.skill?.trim()
  );

  const onSubmit = async (data: { skill: { skill: string }[] }) => {
    try {
      const payload = data.skill.map((s: { skill: string }) => ({
        skill:
          s.skill.charAt(0).toUpperCase() +
          s.skill.slice(1).trim(),
      }));

      await skillService.addSkills(payload);

      addToast("Skills added successfully", "success");

      reset({
        skill: [{ skill: "" }],
      });

      onSuccess();
    } catch (err: unknown) {
      const message = (err as { response?: { data?: string } })?.response?.data;
      addToast(
        message || "Failed to add skills",
        "error"
      );
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl py-4">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-4"
      >
        <div className="max-h-[400px] overflow-y-auto pr-2 pt-1">
        {fields.map((field, index) => (
          <div key={field.id}>
            <div className="flex gap-2">
              <input
                onKeyDown={preventSpecialCharKeyDownInTextfieldAllowPlus}
                {...register(`skill.${index}.skill`)}
                autoComplete="off"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 flex-1 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                placeholder={`Skill ${index + 1}`}
              />

              {fields.length > 1 && (
                <button
                  type="button"
                  onClick={() => remove(index)}
                  className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                >
                  Remove
                </button>
              )}
            </div>

            <div className="h-5 mt-1">
                <p className="text-red-500 text-sm">
                    {errors.skill?.[index]?.skill?.message || ""}
                </p>
            </div>
          </div>
        ))}
        </div>

       <div className="flex gap-3 flex-nowrap">
          <button
            type="button"
            disabled={hasEmptyField}
            onClick={() => append({ skill: "" })}
            className={`px-4 py-2 rounded-lg text-white transition ${
              hasEmptyField
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-blue-500 hover:bg-blue-600"
            }`}
          >
             Add Skill
          </button>

          <button
            type="submit"
            disabled={hasEmptyField || isSubmitting}
            className={`px-4 py-2 rounded-lg text-white transition ${
              hasEmptyField || isSubmitting
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-blue-500 hover:bg-blue-600"
            }`}
          >
            {isSubmitting ? "Saving..." : "Save Skills"}
          </button>
        </div>
      </form>
    </div>
  );
}